// Native JS (no deps): validate "looks like Solana address" by base58+32 bytes.
// Includes friendly hints for ETH/TRON formats.

const BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
const BASE58_MAP = (() => {
  const m = Object.create(null);
  for (let i = 0; i < BASE58_ALPHABET.length; i++) m[BASE58_ALPHABET[i]] = i;
  return m;
})();

/**
 * Decode base58 string to Uint8Array.
 * Throws on invalid characters.
 */
function base58Decode(str) {
  if (typeof str !== "string") throw new Error("not a string");
  str = str.trim();
  if (str.length === 0) return new Uint8Array(0);

  // Count leading '1's (they represent leading zero bytes)
  let zeros = 0;
  while (zeros < str.length && str[zeros] === "1") zeros++;

  // Convert base58 -> base256 via big integer-like array math
  const bytes = []; // little-endian base256
  for (let i = zeros; i < str.length; i++) {
    const ch = str[i];
    const val = BASE58_MAP[ch];
    if (val === undefined) throw new Error(`invalid base58 char: ${ch}`);

    let carry = val;
    for (let j = 0; j < bytes.length; j++) {
      carry += bytes[j] * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }

  // Add leading zeros
  const out = new Uint8Array(zeros + bytes.length);
  for (let i = 0; i < zeros; i++) out[i] = 0;
  // reverse little-endian -> big-endian
  for (let i = 0; i < bytes.length; i++) out[out.length - 1 - i] = bytes[i];
  return out;
}

export function validateSolanaAddressNative(input) {
  const s = (input || "").trim();

  if (!s)
    return { ok: false, code: "EMPTY", message: "Enter a Solana address." };

  // Friendly wrong-chain hints
  if (/^0x[0-9a-fA-F]{40}$/.test(s)) {
    return {
      ok: false,
      code: "ETH",
      message: "That looks like an Ethereum address, not Solana.",
    };
  }
  // Tron base58check addresses are typically 34 chars and start with T
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(s)) {
    return {
      ok: false,
      code: "TRON",
      message: "That looks like a TRON address, not Solana.",
    };
  }

  let bytes;
  try {
    bytes = base58Decode(s);
  } catch {
    return {
      ok: false,
      code: "NOT_BASE58",
      message: "Not a valid Solana address (base58 decode failed).",
    };
  }

  if (bytes.length !== 32) {
    return {
      ok: false,
      code: "WRONG_LENGTH",
      message: `Not a Solana address (decoded length ${bytes.length} bytes; Solana addresses are 32 bytes).`,
    };
  }

  // At this point it's a valid base58-encoded 32-byte value (plausible Solana pubkey).
  return {
    ok: true,
    code: "OK",
    message: "Looks like a valid Solana address.",
  };
}
